import React from 'react'
import { ConfigProvider, Tooltip } from 'antd'
import type { ThemeConfig } from 'antd'
import type { TooltipPlacement } from 'antd/es/tooltip'
import styled from 'styled-components'

/** Default: muted trigger (gray circle + dark glyph). */
const DEFAULT_ICON_BG = '#e8e8e8'
const DEFAULT_ICON_GLYPH = '#595959'

/** Hover: Ant Design primary — blue circle + white “i” */
const HOVER_ICON_BG = '#1677ff'
const HOVER_ICON_GLYPH = '#ffffff'

/** Dark overlay: gray-900–aligned for contrast with white copy (see assessment tooltip mockups). */
const TOOLTIP_BG = '#111827'

const assessmentTooltipTheme: ThemeConfig = {
  components: {
    Tooltip: {
      /** Solid dark fill — default token can resolve to a translucent rgba */
      colorBgSpotlight: TOOLTIP_BG,
      colorTextLightSolid: '#ffffff',
      borderRadius: 8
    }
  }
}

/**
 * Use a span (role="button"), not a native `<button>`, so global / Ant Design `button` resets
 * cannot apply the primary blue fill. Some resets target `button` and set `background` to primary.
 */
const Trigger = styled.span<{ $bg: string; $fg: string }>`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${(p) => p.$bg} !important;
    color: ${(p) => p.$fg} !important;
    font-size: 12px;
    font-weight: 600;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  &&:hover {
    background: ${HOVER_ICON_BG} !important;
    background-image: none !important;
    color: ${HOVER_ICON_GLYPH} !important;
  }
`

export function isEmptyTooltipContent(content: React.ReactNode): boolean {
  if (content == null || content === false) {
    return true
  }
  if (typeof content === 'string') {
    return content.trim() === ''
  }
  return false
}

export interface AssessmentInfoTooltipProps {
  /** Text or JSX shown in the tooltip */
  content?: React.ReactNode
  /** Tooltip placement */
  placement?: TooltipPlacement
  /** Trigger circle fill (default muted gray) */
  iconColor?: string
  /** “i” glyph color (default dark gray for muted trigger) */
  iconGlyphColor?: string
  /**
   * Accessible name for the trigger (tooltip content is not exposed as the accessible name;
   * keep this short and action-oriented).
   */
  ariaLabel?: string
  className?: string
}

/**
 * Assessment info trigger: muted gray by default; **primary blue + white “i” on hover**.
 * Pass iconColor / iconGlyphColor to override idle colors. Tooltip body uses a dark theme.
 */
export function AssessmentInfoTooltip({
  content,
  placement = 'top',
  iconColor = DEFAULT_ICON_BG,
  iconGlyphColor = DEFAULT_ICON_GLYPH,
  ariaLabel = 'Scoring guidance',
  className
}: AssessmentInfoTooltipProps): JSX.Element | null {
  if (isEmptyTooltipContent(content)) {
    return null
  }

  return (
    <ConfigProvider theme={assessmentTooltipTheme}>
      <Tooltip
        title={content}
        placement={placement}
        overlayStyle={{ opacity: 1 }}
        overlayInnerStyle={{
          maxWidth: 'min(calc(100vw - 32px), 360px)',
          padding: '8px 12px',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          backgroundColor: TOOLTIP_BG,
          color: '#ffffff',
          opacity: 1
        }}
      >
        <Trigger
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
          className={className}
          $bg={iconColor}
          $fg={iconGlyphColor}
        >
          <span aria-hidden="true">i</span>
        </Trigger>
      </Tooltip>
    </ConfigProvider>
  )
}
